import * as React from "react";
import { SimilarDestinationsCardProps, SimilarDestinationsProps } from "./typings";
import { UitkCarousel } from "uitk-react-carousel";
import { AnalyticsConfig } from "uitk-react-utils";
import { UitkCard, UitkCardLink, UitkCardMedia } from "uitk-react-cards";
import { UitkFigure, UitkFigureAspectRatioType, UitkImage } from "uitk-react-images";
import { SimilarDestination } from "typings/microserviceModels/te-similar-destination-flex-module";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import { UitkScrim } from "uitk-react-scrim";
import { ViewLarge, Viewport, ViewSmall } from "bernie-view";
import { AspectRatioEnum } from "src/stores/thumbor/ThumborStore";
import { TrackedLink } from "components/utility/analytics/TrackedLink";
import { inject } from "mobx-react";
import { MediaAttribution } from "components/utility/MediaAttributionUtil";
import { useLocalization } from "@shared-ui/localization-context";
import { Action, FlexTrackingInfo, sendDelayedTrackEvent } from "components/utility/analytics/FlexAnalyticsUtils";
import LazyLoad from "react-lazyload";
import { UitkHeading } from "uitk-react-text";
import { UitkLayoutGrid, UitkLayoutGridItem } from "uitk-react-layout-grid";

export const SimilarDestinations = inject(
  "thumbor",
  "analytics"
)((props: SimilarDestinationsProps) => {
  const safelyResizeImage = (destination: SimilarDestination) => {
    const image: string = destination.media?.mediaUrl;

    return (
      props.thumbor?.safelyGenerateThumborizedImageUrl(
        image,
        330,
        190,
        props.context,
        AspectRatioEnum.DEFAULT,
        true,
        100
      ) || image
    );
  };

  const destinations = !props.flexComponent.model.empty && props.flexComponent.model?.content?.items;

  if (!destinations || !destinations.length) {
    return null;
  }

  const { formatText } = useLocalization();
  const destinationKeyHelper = new ItemKeyHelper("similar-destinations");

  const context = props.context;

  const analytics: AnalyticsConfig = {
    id: "TG.LP.Dest.OtherPlaces",
    callback: (id, description) => {
      const flexTrackingInfo: FlexTrackingInfo = {
        moduleName: id,
        action: Action.SCROLL,
        linkName: description
      };
      sendDelayedTrackEvent(flexTrackingInfo, props.analytics);
    }
  };

  const localisedHeading = `${formatText("similarDestinations.title", props.context.searchContext.location.name)}`;

  const carouselProps = {
    itemsVisible: { lg: 1, md: 1, sm: 1 },
    pageBy: 1,
    peekWidth: 25,
    buttonText: {
      nextButton: formatText("carousel.item.next"),
      prevButton: formatText("carousel.item.prev")
    }
  };

  return (
    <Viewport>
      <ViewSmall>
        <div
          id={props.flexComponent.id}
          className="SimilarDestinations SimilarDestinations--carousel uitk-cell all-cell-1-1 all-b-margin-six"
          data-fm={props.flexComponent.fmId}
          data-fm-title-id={props.flexComponent.fmTitleId}
        >
          <UitkCarousel
            className="all-t-margin-zero"
            headline={localisedHeading}
            analytics={analytics}
            carouselName={"SimilarDestinations"}
            {...carouselProps}
          >
            {destinations.map((destination: SimilarDestination, index: number) => (
              <SimilarDestinationsCard
                key={destinationKeyHelper.next()}
                destination={destination}
                context={context}
                position={index + 1}
                destinationImageUrl={safelyResizeImage(destination)}
                className="uitk-carousel-item"
                scrimClassName="uitk-type-display-700"
              />
            ))}
          </UitkCarousel>
        </div>
      </ViewSmall>
      <ViewLarge>
        <div
          id={props.flexComponent.id}
          className="SimilarDestinations uitk-cell all-cell-1-1 all-b-margin-six"
          data-fm={props.flexComponent.fmId}
        >
          <div className="all-b-margin-three">
            <UitkHeading size={600} level={3}>
              {localisedHeading}
            </UitkHeading>
          </div>
          <UitkLayoutGrid columns={4} space="three">
            {destinations.map((destination: SimilarDestination, index: number) => (
              <UitkLayoutGridItem key={destinationKeyHelper.next()}>
                <SimilarDestinationsCard
                  key={destinationKeyHelper.next()}
                  destination={destination}
                  context={context}
                  position={index + 1}
                  destinationImageUrl={safelyResizeImage(destination)}
                  className=""
                  scrimClassName="uitk-type-heading-600"
                />
              </UitkLayoutGridItem>
            ))}
          </UitkLayoutGrid>
        </div>
      </ViewLarge>
    </Viewport>
  );
});

export const SimilarDestinationsCard = ({
  destination,
  position,
  destinationImageUrl,
  className,
  scrimClassName
}: SimilarDestinationsCardProps) => {
  const { media, link, localisedRegionName } = destination;
  const { formatText } = useLocalization();

  const placeholder = (
    <UitkImage alt={formatText("hotels.imagePlaceHolder", localisedRegionName)} placeholderImage={true} />
  );

  const attribution = MediaAttribution(media, localisedRegionName);

  return (
    <UitkCard className={className} key={position}>
      <UitkCardMedia>
        <UitkCardLink>
          <TrackedLink moduleName="TG.LP" href={link} rfrr="Dest.OtherPlaces" linkName={localisedRegionName}>
            {localisedRegionName}
          </TrackedLink>
        </UitkCardLink>
        <UitkFigure ratio={UitkFigureAspectRatioType.R16_9}>
          <LazyLoad placeholder={placeholder} offset={250}>
            <UitkImage alt={attribution?.attributionAltText} src={destinationImageUrl} />
          </LazyLoad>
          <UitkScrim className={scrimClassName}>{localisedRegionName}</UitkScrim>
          {attribution?.attributionText}
        </UitkFigure>
      </UitkCardMedia>
    </UitkCard>
  );
};

export default SimilarDestinations;
